import AddIcon from "@mui/icons-material/Add";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import DeleteIco from "../../../../assets/icons/DeleteIco";
import JobPenIco from "../../../../assets/icons/JobPenIco";
import JobStarIco from "../../../../assets/icons/JobStarIco";
import XCloseIco from "../../../../assets/icons/XCloseIco";
import CommonButton from "../../../../component/Button";
import CommonTextField from "../../../../component/Input";
import { CommonGrid2 } from "../../../common-components/StyledComponents";
import { CommonTippy, CustomizeSpan } from "../../style";
import QuestionMarkIco from "../../../../assets/icons/QuestionMarkIco";
import PreStarIco from "../../../../assets/icons/PreStarIco";
import PrePenIco from "../../../../assets/icons/PrePenIco";

const AddPreScreenQuestions = ({
  handleDialogClose,
  handleSubmit,
  data = [],
  preScreen,
}) => {
  // API now returns [{ text, type, includeInCoversheet }]
  // Split into recommended and userAdded based on type
  const allQuestions = (data || []).map((item) => {
    // Handle both old string format and new object format
    if (typeof item === "string") {
      return { text: item, type: "recommended", include_in_coversheet: true };
    }
    return {
      text: item.text || "",
      type: item.type || "recommended",
      include_in_coversheet: item.include_in_coversheet ?? true,
    };
  });

  const recommendedQuestions = allQuestions.filter(
    (q) => q.type === "recommended"
  );
  const userAddedQuestions = allQuestions.filter((q) => q.type === "user");

  const allChecked =
    allQuestions.length > 0 &&
    allQuestions.every((q) => q.include_in_coversheet === true);

  const initialValues = {
    recommended: recommendedQuestions,
    userAdded: userAddedQuestions,
    includeAll: allChecked,
  };

  const validationSchema = Yup.object({
    userAdded: Yup.array().of(
      Yup.object({
        text: Yup.string()
          .required("Question is required")
          .matches(/^[^\s].*$/, "Question cannot start with spaces")
          .test(
            "no-whitespace",
            "Question cannot be just whitespace",
            (value) => value && value.trim().length > 0
          ),
        // includeIn_coversheet: Yup.boolean(),
      })
    ),
  });

  const handleAddQuestion = (setFieldValue, values) => {
    setFieldValue("userAdded", [
      ...values.userAdded,
      { text: "", include_in_coversheet: true },
    ]);
  };

  const handleRemoveQuestion = (setFieldValue, values, index) => {
    const updated = values.userAdded.filter((_, i) => i !== index);
    setFieldValue("userAdded", updated);
    const allRecommended = values.recommended.every(
      (q) => q.include_in_coversheet
    );
    const allUser =
      updated.length === 0 || updated.every((q) => q.include_in_coversheet);
    setFieldValue(
      "includeAll",
      values.recommended.length + updated.length > 0 &&
        allRecommended &&
        allUser
    );
  };

  const handleIncludeAllChange = (setFieldValue, values, checked) => {
    setFieldValue("includeAll", checked);
    setFieldValue(
      "recommended",
      values.recommended.map((q) => ({ ...q, include_in_coversheet: checked }))
    );
    setFieldValue(
      "userAdded",
      values.userAdded.map((q) => ({ ...q, include_in_coversheet: checked }))
    );
  };

  const recomputeIncludeAll = (values, changedList, changedField) => {
    const recommended =
      changedField === "recommended" ? changedList : values.recommended;
    const userAdded =
      changedField === "userAdded" ? changedList : values.userAdded;
    const all = [...recommended, ...userAdded];
    return all.length > 0 && all.every((q) => q.include_in_coversheet);
  };

  const onSubmit = (values) => {
    const questions = [
      ...values.recommended.map((q) => ({
        text: q.text,
        type: "recommended",
        include_in_coversheet: q.include_in_coversheet,
      })),
      ...values.userAdded.map((q) => ({
        text: q.text,
        type: "user",
        include_in_coversheet: q.include_in_coversheet,
      })),
    ];
    handleSubmit({ questions });
  };

  return (
    <Dialog
      fullWidth
      maxWidth={"md"}
      open={preScreen?.open}
      onClose={handleDialogClose}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
          background: "#FFF",
          boxShadow:
            "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, setFieldValue,dirty }) => (
          <Form>
            {/* ── Header ── */}
            <DialogActions
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px 8px 24px",
              }}
            >
              <DialogTitle sx={{ padding: 0 }}>
                <CommonGrid2 display="flex" alignItems="center" gap="8px">
                  <QuestionMarkIco
                    sx={{ color: "#7F56D9", fontSize: "20px" }}
                  />
                  <Typography
                    variant="h3"
                    fontSize={18}
                    fontWeight={600}
                    fontFamily="Inter"
                    color="#101828"
                  >
                    Recommended Pre-screen questions
                  </Typography>
                </CommonGrid2>
              </DialogTitle>

              <CommonGrid2 display="flex" alignItems="center" gap="8px">
                <Button onClick={handleDialogClose} sx={{ minWidth: "auto" }}>
                  <XCloseIco />
                </Button>
              </CommonGrid2>
            </DialogActions>

            {/* ── Include All ── */}

            {/* ── Body ── */}
            <DialogContent sx={{ paddingTop: "8px" }}>
              <Grid2 spacing={1.4} container>
                {/* ── Recommended questions (read-only) ── */}
                {values.recommended.map((item, index) => (
                  <CommonGrid2 size={12} key={`recommended-${index}`}>
                    <CommonGrid2
                      justifyContent={"space-between"}
                      gap={"1rem"}
                      display={"flex"}
                      paddingRight={index === 0 ? "12px" : "0px"}
                    >
                      <CommonGrid2
                        display="flex"
                        alignItems="center"
                        gap="8px"
                        width={"100%"}
                      >
                        <Typography
                          fontSize={"body1"}
                          fontWeight={700}
                          color="#101828"
                        >
                          Question {index + 1}
                        </Typography>
                        <CommonTippy
                          content={
                            <CustomizeSpan
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <PreStarIco />
                              Recommended
                            </CustomizeSpan>
                          }
                          placement="right"
                          color="#0070F2"
                          fontWeight={600}
                          fontSize={"13px"}
                        >
                          <IconButton>
                            <JobStarIco size={21} />
                          </IconButton>
                        </CommonTippy>
                      </CommonGrid2>
                      {index === 0 && (
                        <CommonGrid2
                          display={"flex"}
                          alignItems={"center"}
                          justifyContent={"end"}
                          width={"95.5%"}
                        >
                          <Typography
                            fontSize={14}
                            fontWeight={500}
                            fontFamily="Inter"
                            color="#344054"
                          >
                            Include All
                          </Typography>
                          <Checkbox
                            checked={values.includeAll}
                            onChange={(e) =>
                              handleIncludeAllChange(
                                setFieldValue,
                                values,
                                e.target.checked
                              )
                            }
                            disabled={preScreen.disabled}
                            sx={{
                              color: "#D0D5DD",
                              "&.Mui-checked": { color: "#7F56D9" },
                              padding: "4px",
                            }}
                          />
                        </CommonGrid2>
                      )}
                    </CommonGrid2>

                    <CommonGrid2
                      display="flex"
                      alignItems="center"
                      gap="1rem"
                      border="1px solid #EAECF0"
                      borderRadius="8px"
                      padding="10px "
                      backgroundColor="#FFFFFF"
                    >
                      <Typography flex={1} fontSize={"body1"} color="#344054">
                        {item.text}
                      </Typography>

                      <CommonGrid2
                        display="flex"
                        alignItems="center"
                        gap="6px"
                        flexShrink={0}
                      >
                        <Typography
                          fontSize={"subtitle3"}
                          color="#344054"
                          whiteSpace="nowrap"
                        >
                          Include in Coversheet
                        </Typography>
                        <Checkbox
                          checked={item.include_in_coversheet}
                          onChange={(e) => {
                            const updated = values.recommended.map((q, i) =>
                              i === index
                                ? {
                                    ...q,
                                    include_in_coversheet: e.target.checked,
                                  }
                                : q
                            );
                            setFieldValue("recommended", updated);
                            setFieldValue(
                              "includeAll",
                              recomputeIncludeAll(
                                values,
                                updated,
                                "recommended"
                              )
                            );
                          }}
                          disabled={preScreen.disabled}
                          sx={{
                            color: "#D0D5DD",
                            "&.Mui-checked": { color: "#7F56D9" },
                            padding: "4px",
                          }}
                        />
                      </CommonGrid2>
                    </CommonGrid2>
                  </CommonGrid2>
                ))}

                {/* ── User-added questions (editable) ── */}
                {values.userAdded.map((item, index) => {
                  const questionNumber = values.recommended.length + index + 1;
                  return (
                    <CommonGrid2
                      size={12}
                      key={`user-${index}`}
                      paddingRight="12px"
                    >
                      <CommonGrid2 display="flex" alignItems="center" gap="8px">
                        <Typography
                          fontSize={"body1"}
                          fontWeight={700}
                          color="#101828"
                        >
                          Question {questionNumber}
                        </Typography>
                        <CommonTippy
                          content={
                            <CustomizeSpan
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <PrePenIco />
                              User Added
                            </CustomizeSpan>
                          }
                          placement="right"
                          color="#049F9A"
                          fontWeight={600}
                          fontSize={"13px"}
                        >
                          <IconButton>
                            <JobPenIco />
                          </IconButton>
                        </CommonTippy>
                      </CommonGrid2>

                      <CommonGrid2 display="flex" alignItems="start" gap="1rem">
                        <Field name={`userAdded.${index}.text`}>
                          {({ field }) => (
                            <CommonTextField
                              {...field}
                              placeholder="Enter the Question"
                              padding="10px"
                              fontWeight={400}
                              fontSize={"14px"}
                              multiline
                              minRows={1}
                              maxRows={5}
                              error={Boolean(
                                errors?.userAdded?.[index]?.text &&
                                  touched?.userAdded?.[index]?.text
                              )}
                              helperText={
                                Boolean(
                                  errors?.userAdded?.[index]?.text &&
                                    touched?.userAdded?.[index]?.text
                                ) && errors?.userAdded?.[index]?.text
                              }
                              customStyle={{
                                flex: 1,
                                "& .MuiInputBase-root": {
                                  padding: "0px",
                                },
                              }}
                            />
                          )}
                        </Field>
                        <CommonGrid2
                          display="flex"
                          alignItems="center"
                          gap="1rem"
                        >
                          <IconButton
                            size="small"
                            sx={{
                              border: "1px solid #D0D5DD",
                              borderRadius: "7px",
                              padding: "10px",
                              flexShrink: 0,
                            }}
                            onClick={() =>
                              handleRemoveQuestion(setFieldValue, values, index)
                            }
                            disabled={preScreen.disabled}
                          >
                            <DeleteIco stroke={"#D80027"} />
                          </IconButton>

                          <CommonGrid2
                            display="flex"
                            alignItems="center"
                            gap="6px"
                            flexShrink={0}
                          >
                            <Typography
                              fontSize={13}
                              fontFamily="Inter"
                              fontWeight={500}
                              color="#344054"
                              whiteSpace="nowrap"
                            >
                              Include in Coversheet
                            </Typography>
                            <Checkbox
                              checked={item.include_in_coversheet}
                              onChange={(e) => {
                                const updated = values.userAdded.map((q, i) =>
                                  i === index
                                    ? {
                                        ...q,
                                        include_in_coversheet: e.target.checked,
                                      }
                                    : q
                                );
                                setFieldValue("userAdded", updated);
                                setFieldValue(
                                  "includeAll",
                                  recomputeIncludeAll(
                                    values,
                                    updated,
                                    "userAdded"
                                  )
                                );
                              }}
                              disabled={preScreen.disabled}
                              sx={{
                                color: "#D0D5DD",
                                "&.Mui-checked": { color: "#7F56D9" },
                                padding: "4px",
                              }}
                            />
                          </CommonGrid2>
                        </CommonGrid2>
                      </CommonGrid2>
                    </CommonGrid2>
                  );
                })}

                {/* ── Add Question ── */}
                <Grid2 size={12} marginTop={"0.5rem"}>
                  <CommonButton
                    value={"Add Question"}
                    variant={"outlined"}
                    startIcon={<AddIcon sx={{ color: "#7F56D9" }} />}
                    color={"#344054"}
                    padding={"8px 1.5rem"}
                    fontWeight={600}
                    fontSize={"14px"}
                    gap={"0px"}
                    svg={true}
                    disabled={preScreen.disabled}
                    onClick={() => handleAddQuestion(setFieldValue, values)}
                  />
                </Grid2>

                {/* ── Footer ── */}
                <CommonGrid2
                  display={"flex"}
                  gap={"1rem"}
                  justifyContent={"end"}
                  size={12}
                >
                  <CommonButton
                    value={"Cancel"}
                    variant="outlined"
                    padding={"10px 1.5rem"}
                    fontWeight={"700"}
                    fontSize={"15px"}
                    color="#344054"
                    border="#D0D5DD"
                    onClick={handleDialogClose}
                    disabled={preScreen.disabled}
                  />
                  <CommonButton
                    value={"Save"}
                    padding={"10px 1.5rem"}
                    fontWeight={"700"}
                    fontSize={"15px"}
                    type="submit"
                    disabled={preScreen.disabled || !dirty}
                    loading={preScreen.saveLoading}
                  />
                </CommonGrid2>
              </Grid2>
            </DialogContent>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddPreScreenQuestions;
